import { create } from "zustand";
import { persist } from "zustand/middleware";
import logger from "../utils/secureLogger";
import {
  fetchAuthSession,
  getCurrentUser,
  fetchUserAttributes,
} from "@aws-amplify/auth";
import {
  hasPermission,
  getUserHighestRole,
  AUTH_CONFIG,
} from "@/features/auth/config/authConfig";

// Session timeout configuration (in milliseconds)
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const SESSION_WARNING_TIME = 5 * 60 * 1000; // 5 minutes before timeout

export const useAuthStore = create(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      currentUser: null,
      userGroups: [],
      loading: true,
      sessionTimer: null,
      sessionWarningTimer: null,
      showSessionWarning: false,

      setLoading: (loading) => set({ loading }),

      login: (userData) => {
        if (!userData || !userData.username) {
          throw new Error("User data is required");
        }

        set({
          isAuthenticated: true,
          currentUser: userData,
          userGroups: userData.groups || [],
          loading: false,
        });

        // Start session timer after successful login
        get().startSessionTimer();
      },

      logout: async () => {
        try {
          logger.debug("🚪 Starting logout process...");
          set({ loading: true });
          
          // Clear session timers first
          get().clearSessionTimers();
          
          // Import authService dynamically to avoid circular dependency
          const { default: authService } = await import("@/features/auth/services/authService");
          
          // Perform logout (local only to avoid redirect issues)
          await authService.signOut();
          
          // Clear chat sessions on logout
          try {
            const { useHealthOpsChatStore } = await import("@/stores/healthOpsChatStore");
            useHealthOpsChatStore.getState().clearAllSessions();
            logger.debug("🧹 Chat sessions cleared on logout");
          } catch (error) {
            logger.warn("⚠️ Could not clear chat sessions:", error);
          }
          
          // Clear store state
          set({
            isAuthenticated: false,
            currentUser: null,
            userGroups: [],
            loading: false,
          });
          
          logger.debug("✅ Logout completed successfully");
          
          // Use navigate instead of window.location to avoid full page reload
          // This will be handled by the component that calls logout
          
        } catch (error) {
          logger.error("❌ Logout error:", error);
          
          // Force clear state even if logout fails
          get().clearSessionTimers();
          
          // Try to clear chat sessions even on error
          try {
            const { useHealthOpsChatStore } = await import("@/stores/healthOpsChatStore");
            useHealthOpsChatStore.getState().clearAllSessions();
          } catch (chatError) {
            logger.warn("⚠️ Could not clear chat sessions on error:", chatError);
          }
          
          set({
            isAuthenticated: false,
            currentUser: null,
            userGroups: [],
            loading: false,
          });
          
          logger.debug("⚠️ Forced logout completed due to error");
        }
      },

      hasPermission: (requiredPage) => {
        const { userGroups } = get();
        return hasPermission(userGroups, requiredPage);
      },

      getUserHighestRole: () => {
        const { userGroups } = get();
        return getUserHighestRole(userGroups);
      },

      getUserDisplayName: () => {
        const { currentUser } = get();

        if (!currentUser) return "User";

        return (
          currentUser.email ||
          currentUser.username ||
          currentUser.attributes?.email ||
          "User"
        );
      },

      initializeAuth: async () => {
        const currentState = get();
        
        // Avoid multiple simultaneous initializations
        if (currentState.loading) {
          logger.debug("🔄 Auth initialization already in progress, waiting...");
          // Wait for current initialization to complete instead of skipping
          let attempts = 0;
          while (get().loading && attempts < 30) { // Reduced from 50 to 30 (3 seconds)
            await new Promise(resolve => setTimeout(resolve, 100));
            attempts++;
          }
          
          if (attempts >= 30) {
            logger.debug("⚠️ Auth initialization timeout, forcing reset...");
            set({ loading: false });
          } else {
            logger.debug("✅ Auth initialization completed by another call");
            return;
          }
        }

        try {
          set({ loading: true });
          logger.debug("🔧 Initializing authentication...");
          
          const session = await fetchAuthSession({ forceRefresh: false }); // Don't force refresh on init

          if (session.tokens && session.tokens.accessToken) {
            logger.debug("✅ Valid session found, extracting user data from tokens...");
            
            // Extract user data directly from tokens to avoid API calls that might fail
            const userData = get().extractUserDataFromSession(session);

            set({
              isAuthenticated: true,
              currentUser: userData,
              userGroups: userData.groups,
              loading: false,
            });
            
            // Start session timer after successful initialization
            get().startSessionTimer();
            
            logger.debug("✅ Auth initialization completed successfully");
            logger.debug("👤 User:", userData.username);
            logger.debug("👥 Groups:", userData.groups);
          } else {
            logger.debug("❌ No valid session found");
            set({
              isAuthenticated: false,
              currentUser: null,
              userGroups: [],
              loading: false,
            });
          }
        } catch (error) {
          logger.debug("❌ Auth initialization failed:", error.name, error.message);
          
          // Clear potentially corrupted tokens
          if (error.name === 'NotAuthorizedException' || 
              error.message.includes('token') || 
              error.message.includes('expired')) {
            logger.debug("🧹 Clearing expired tokens due to auth error...");
            get().clearAuthTokens();
          }
          
          set({
            isAuthenticated: false,
            currentUser: null,
            userGroups: [],
            loading: false,
          });
        }
      },

      // Emergency reset method
      resetAuthState: () => {
        logger.debug("🔄 Resetting auth state...");
        set({
          isAuthenticated: false,
          currentUser: null,
          userGroups: [],
          loading: false,
        });
      },

      extractUserGroups: (session) => {
        const { idToken, accessToken } = session.tokens || {};
        const groupsFromIdToken = idToken?.payload?.["cognito:groups"];
        const groupsFromAccessToken = accessToken?.payload?.["cognito:groups"];

        return (
          groupsFromIdToken ||
          groupsFromAccessToken || [AUTH_CONFIG.USER_GROUPS.DEFAULT]
        );
      },

      // New method to extract user data from session tokens
      extractUserDataFromSession: (session) => {
        const { idToken } = session.tokens || {};
        
        if (!idToken) {
          throw new Error("No ID token available");
        }

        const payload = idToken.payload;
        const groups = get().extractUserGroups(session);

        return {
          username: payload.email || payload['cognito:username'] || payload.sub,
          email: payload.email,
          attributes: {
            email: payload.email,
            given_name: payload.given_name,
            family_name: payload.family_name,
            name: payload.name,
            'custom:Group': payload['custom:Group']
          },
          groups,
          sub: payload.sub
        };
      },

      // Session management methods
      startSessionTimer: () => {
        const state = get();
        
        // Clear existing timers
        if (state.sessionTimer) clearTimeout(state.sessionTimer);
        if (state.sessionWarningTimer) clearTimeout(state.sessionWarningTimer);
        
        logger.debug("⏰ Starting session timer...");
        
        // Set warning timer (25 minutes)
        const warningTimer = setTimeout(() => {
          logger.debug("⚠️ Session warning triggered");
          set({ showSessionWarning: true });
        }, SESSION_TIMEOUT - SESSION_WARNING_TIME);
        
        // Set logout timer (30 minutes)
        const logoutTimer = setTimeout(async () => {
          logger.debug("⏰ Session timeout - auto logout");
          await get().logout();
        }, SESSION_TIMEOUT);
        
        set({
          sessionTimer: logoutTimer,
          sessionWarningTimer: warningTimer,
          showSessionWarning: false
        });
      },

      extendSession: () => {
        logger.debug("🔄 Extending session...");
        set({ showSessionWarning: false });
        get().startSessionTimer();
      },

      dismissSessionWarning: () => {
        set({ showSessionWarning: false });
      },

      // New method to clear auth tokens
      clearAuthTokens: () => {
        try {
          logger.debug("🧹 Clearing auth tokens...");
          localStorage.removeItem('amplify-signin-with-hostedUI');
          localStorage.removeItem('amplify-redirected-from-hosted-ui');
          
          const keys = Object.keys(localStorage);
          keys.forEach(key => {
            if (key.startsWith('CognitoIdentityServiceProvider.')) {
              localStorage.removeItem(key);
            }
          });
          
          logger.debug("✅ Auth tokens cleared");
        } catch (error) {
          logger.debug("❌ Error clearing tokens:", error);
        }
      },

      clearSessionTimers: () => {
        const state = get();
        if (state.sessionTimer) {
          clearTimeout(state.sessionTimer);
        }
        if (state.sessionWarningTimer) {
          clearTimeout(state.sessionWarningTimer);
        }
        set({
          sessionTimer: null,
          sessionWarningTimer: null,
          showSessionWarning: false
        });
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        currentUser: state.currentUser,
        userGroups: state.userGroups,
      }),
    }
  )
);
